import React, { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import AppointmentChip from './AppointmentChip';
import { buildMonthCells, sameDay } from './dateUtils';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * MonthGrid — unchanged except for the new `bare` prop, which drops the
 * outer border/radius so it can sit flush inside CalendarPage's merged
 * toolbar card.
 */
const MonthGrid = ({ anchor, appts, onApptClick, onSlotClick, bare = false }) => {
  const cells = useMemo(() => buildMonthCells(anchor), [anchor]);
  const today = new Date();
  const month = anchor.getMonth();

  const byDay = useMemo(() => {
    const map = new Map();
    appts.forEach((a) => {
      const key = new Date(a.start_time).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [appts]);

  return (
    <Box
      sx={{
        border: bare ? 0 : 1,
        borderColor: 'divider',
        borderRadius: bare ? 0 : 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {WEEKDAY_LABELS.map((label) => (
          <Box
            key={label}
            sx={{
              py: 1,
              textAlign: 'center',
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              {label}
            </Typography>
          </Box>
        ))}
        {cells.map((day, idx) => {
          const inMonth = day.getMonth() === month;
          const isToday = sameDay(day, today);
          const dayAppts = byDay.get(day.toDateString()) || [];
          const max = 3;
          const overflow = dayAppts.length - max;
          return (
            <Box
              key={idx}
              onClick={() => {
                if (!onSlotClick) return;
                const dt = new Date(day);
                dt.setHours(9, 0, 0, 0);
                onSlotClick(dt);
              }}
              sx={{
                minHeight: 110,
                p: 0.75,
                borderRight: (idx + 1) % 7 === 0 ? 0 : 1,
                borderBottom: idx < 35 ? 1 : 0,
                borderColor: 'divider',
                bgcolor: inMonth ? 'background.paper' : 'action.hover',
                cursor: onSlotClick ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                opacity: inMonth ? 1 : 0.55,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    bgcolor: isToday ? 'primary.main' : 'transparent',
                    color: isToday ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant="caption" fontWeight={700}>
                    {day.getDate()}
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={0.25} sx={{ overflow: 'hidden' }}>
                {dayAppts.slice(0, max).map((a) => (
                  <AppointmentChip
                    key={a.appointment_id}
                    appt={a}
                    onClick={onApptClick}
                    compact
                  />
                ))}
                {overflow > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                    +{overflow} more
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthGrid;
